"use client";

import { useEffect, useMemo, useState } from "react";
import { calculateFromClientPrice } from "@/lib/calculator.mjs";
import { Icon } from "./Icons";
import {
  Field,
  PageHeader,
  addDays,
  formatMoney,
  todayIso
} from "./UI";

const SCENARIO_COPY = {
  taxFree: {
    eyebrow: "Reference",
    title: "No taxes or fees",
    text: "What the rate looks like with no VAT, tourist tax or Booking costs.",
    badge: "Benchmark"
  },
  normal: {
    eyebrow: "Recommended",
    title: "Standard price",
    text: "VAT is included, Booking costs are absorbed, and tourist tax is separate.",
    badge: "Default"
  },
  clientPays: {
    eyebrow: "Alternative",
    title: "Client pays taxes",
    text: "VAT and tourist tax are added on top of the accommodation rate.",
    badge: "Taxes added"
  }
};

function findTariff(tariffs, roomId, date) {
  const matches = tariffs.filter(
    (tariff) =>
      tariff.active &&
      (!tariff.roomId || tariff.roomId === roomId) &&
      (!tariff.startDate || tariff.startDate <= date) &&
      (!tariff.endDate || tariff.endDate >= date)
  );
  return (
    matches.find((tariff) => tariff.roomId === roomId) || matches[0] || null
  );
}

export default function CalculatorPage({ data, actions }) {
  const activeRooms = data.rooms.filter((room) => room.status === "active");
  const [roomId, setRoomId] = useState(activeRooms[0]?.id || "");
  const [checkIn, setCheckIn] = useState(todayIso());
  const [nights, setNights] = useState(1);
  const [clientRoomPrice, setClientRoomPrice] = useState(
    activeRooms[0]?.targetNet || 466.75
  );
  const [commissionEnabled, setCommissionEnabled] = useState(true);
  const [combinePromotions, setCombinePromotions] = useState(
    () =>
      data.settings.stackingMode === "stack" ||
      data.settings.promotionBehaviorVersion !== 2
  );
  const [selectedScenario, setSelectedScenario] = useState("normal");
  const [promotionIds, setPromotionIds] = useState(() =>
    data.promotions.filter((promotion) => promotion.active).map((item) => item.id)
  );

  const room = activeRooms.find((item) => item.id === roomId);
  const tariff = useMemo(
    () => findTariff(data.tariffs, roomId, checkIn),
    [data.tariffs, roomId, checkIn]
  );

  useEffect(() => {
    if (!room) return;
    const date = new Date(`${checkIn}T12:00:00`);
    const isWeekend = date.getDay() === 5 || date.getDay() === 6;
    const tariffPrice = tariff
      ? Number(tariff.targetNet || room.targetNet) +
        (isWeekend ? Number(tariff.weekendAdjustment || 0) : 0)
      : Number(room.targetNet);
    setClientRoomPrice(tariffPrice);
  }, [roomId, checkIn, room, tariff]);

  const selectedPromotions = data.promotions.map((promotion) => ({
    ...promotion,
    active: promotionIds.includes(promotion.id)
  }));

  const result = useMemo(
    () =>
      calculateFromClientPrice({
        clientRoomPrice,
        nights,
        commissionEnabled,
        accommodationVatRate: data.settings.accommodationVatRate,
        touristTaxRate: data.settings.touristTaxRate,
        commissionRate: data.settings.commissionRate,
        commissionVatRate: data.settings.commissionVatRate,
        commissionVatRecoverable: data.settings.commissionVatRecoverable,
        commissionBasis: data.settings.commissionBasis,
        stackingMode: combinePromotions ? "stack" : "best",
        promotions: selectedPromotions
      }),
    [
      clientRoomPrice,
      nights,
      commissionEnabled,
      combinePromotions,
      data.settings,
      promotionIds,
      data.promotions
    ]
  );

  const selected = result[selectedScenario];
  const currency = data.settings.currency;
  const checkout = addDays(checkIn, Number(nights));

  function chooseRoom(event) {
    setRoomId(event.target.value);
  }

  function togglePromotion(id) {
    setPromotionIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  }

  return (
    <div className="page calculator-page">
      <PageHeader
        eyebrow="Nightly rate calculator"
        title="Price with confidence"
        text="Enter the client’s final nightly total. We split out promotions, taxes, Booking costs and what the hotel keeps."
        action={
          <button
            className="button button-secondary desktop-only"
            onClick={() => actions.navigate("admin")}
          >
            <Icon name="settings" size={18} />
            Pricing settings
          </button>
        }
      />

      <section className="calculator-layout">
        <div className="calculator-controls card">
          <div className="section-heading compact">
            <div>
              <span className="step-number">1</span>
              <div>
                <h2>Stay details</h2>
                <p>Select the room and final client total.</p>
              </div>
            </div>
          </div>

          <div className="form-grid">
            <Field label="Room">
              <select value={roomId} onChange={chooseRoom}>
                {activeRooms.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.code} · {item.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Check-in">
              <input
                type="date"
                value={checkIn}
                onChange={(event) => setCheckIn(event.target.value)}
              />
            </Field>
            <Field label="Nights" hint={`Check-out ${checkout}`}>
              <div className="number-stepper">
                <button
                  type="button"
                  onClick={() => setNights((value) => Math.max(1, value - 1))}
                  aria-label="Remove one night"
                >
                  −
                </button>
                <input
                  inputMode="numeric"
                  min="1"
                  max="365"
                  type="number"
                  value={nights}
                  onChange={(event) =>
                    setNights(Math.max(1, Number(event.target.value)))
                  }
                />
                <button
                  type="button"
                  onClick={() => setNights((value) => value + 1)}
                  aria-label="Add one night"
                >
                  +
                </button>
              </div>
            </Field>
            <Field
              className="target-field"
              label="Client total / night"
              hint={
                tariff
                  ? `${tariff.name} applied · standard scenario`
                  : "Final amount paid in the standard scenario"
              }
            >
              <div className="money-input">
                <input
                  inputMode="decimal"
                  min="0"
                  step="10"
                  type="number"
                  value={clientRoomPrice}
                  onChange={(event) =>
                    setClientRoomPrice(Number(event.target.value))
                  }
                />
                <span>{currency}</span>
              </div>
            </Field>
          </div>

          <div className="quick-adjust">
            <span>Quick adjust</span>
            <div>
              {[-20, -10, 10, 20].map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() =>
                    setClientRoomPrice((value) =>
                      Math.max(0, Number(value) + amount)
                    )
                  }
                >
                  {amount > 0 ? "+" : ""}
                  {amount} {currency}
                </button>
              ))}
            </div>
          </div>

          <div className="section-divider" />

          <label className="commission-toggle">
            <span className="commission-toggle-icon">
              <Icon name="tag" size={18} />
            </span>
            <span>
              <strong>Booking.com commission</strong>
              <small>
                {commissionEnabled
                  ? `${data.settings.commissionRate}% commission is deducted`
                  : "No Booking commission is deducted"}
              </small>
            </span>
            <input
              type="checkbox"
              checked={commissionEnabled}
              onChange={(event) => setCommissionEnabled(event.target.checked)}
            />
            <i />
          </label>

          <div className="section-divider" />

          <div className="section-heading compact">
            <div>
              <span className="step-number">2</span>
              <div>
                <h2>Promotions</h2>
                <p>
                  {combinePromotions
                    ? "Selected discounts stack sequentially."
                    : "Only the largest selected discount is used."}
                </p>
              </div>
            </div>
            <strong className="discount-total">
              −{(result.effectiveDiscount * 100).toFixed(1)}%
            </strong>
          </div>
          <label className="promotion-stack-toggle">
            <span>
              <Icon name="layers" size={17} />
              Combine selected promotions
            </span>
            <input
              type="checkbox"
              checked={combinePromotions}
              onChange={(event) => setCombinePromotions(event.target.checked)}
            />
            <i />
          </label>
          {result.effectiveDiscount > 0 ? (
            <div className="promotion-applied">
              <Icon name="check" size={17} />
              <span>
                Promotion applied. Client pays{" "}
                <strong>{formatMoney(clientRoomPrice, currency)}</strong> after{" "}
                <strong>
                  {formatMoney(result.promotionSavings, currency)}
                </strong>{" "}
                discount.
              </span>
            </div>
          ) : (
            <div className="promotion-applied muted-promotion">
              <Icon name="info" size={17} />
              <span>No promotion selected.</span>
            </div>
          )}

          <div className="promotion-picker">
            {data.promotions.map((promotion) => {
              const selected = promotionIds.includes(promotion.id);
              return (
                <button
                  className={selected ? "selected" : ""}
                  key={promotion.id}
                  type="button"
                  onClick={() => togglePromotion(promotion.id)}
                >
                  <span>
                    <Icon name={selected ? "check" : "tag"} size={17} />
                  </span>
                  <div>
                    <strong>{promotion.name}</strong>
                    <small>{promotion.channel}</small>
                  </div>
                  <b>−{promotion.discount}%</b>
                </button>
              );
            })}
          </div>
        </div>

        <div className="calculator-summary">
          <div className="summary-intro">
            <span className="eyebrow">Rate recommendation</span>
            <h2>Three ways to price {room?.name || "this room"}</h2>
            <p>
              Based on a final client total of{" "}
              <strong>{formatMoney(clientRoomPrice, currency)}</strong>.
            </p>
          </div>

          <div className="scenario-list">
            {["taxFree", "normal", "clientPays"].map((key) => {
              const scenario = result[key];
              const copy = SCENARIO_COPY[key];
              return (
                <button
                  className={`scenario-card scenario-${key} ${
                    selectedScenario === key ? "selected" : ""
                  }`}
                  key={key}
                  onClick={() => setSelectedScenario(key)}
                >
                  <div className="scenario-card-top">
                    <div>
                      <span className="scenario-eyebrow">{copy.eyebrow}</span>
                      <h3>{copy.title}</h3>
                    </div>
                    <span className="scenario-badge">{copy.badge}</span>
                  </div>
                  <p>{copy.text}</p>
                  <div className="scenario-main-price">
                    <span>Hotel keeps</span>
                    <strong>{formatMoney(scenario.hotelNet, currency)}</strong>
                    <small>per night after applicable deductions</small>
                  </div>
                  <div className="scenario-footer">
                    <span>
                      Client total
                      <b>{formatMoney(scenario.clientNightlyTotal, currency)}</b>
                    </span>
                    <span>
                      Promotion
                      <b>
                        −{formatMoney(scenario.promotionSavings, currency)}
                      </b>
                    </span>
                    <Icon name="chevron" />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="breakdown-card card">
            <div className="breakdown-heading">
              <div>
                <span className="eyebrow">Selected breakdown</span>
                <h3>{SCENARIO_COPY[selectedScenario].title}</h3>
              </div>
              <span className="nights-badge">
                <Icon name="moon" size={16} />
                {nights} {nights === 1 ? "night" : "nights"}
              </span>
            </div>
            <div className="breakdown-rows">
              <span>
                Listed price before promotion
                <b>{formatMoney(selected.listRate, currency)}</b>
              </span>
              <span className="promotion-breakdown">
                Selected promotion
                <b>−{formatMoney(selected.promotionSavings, currency)}</b>
              </span>
              <span>
                Accommodation after promotion
                <b>{formatMoney(selected.discountedGross, currency)}</b>
              </span>
              <span>
                Accommodation VAT
                <b>
                  {selected.accommodationVat
                    ? `−${formatMoney(selected.accommodationVat, currency)}`
                    : formatMoney(0, currency)}
                </b>
              </span>
              <span>
                Booking commission
                <b>
                  {commissionEnabled && selected.bookingCommission
                    ? `−${formatMoney(selected.bookingCommission, currency)}`
                    : "Off"}
                </b>
              </span>
              {selected.commissionVatCost > 0 ? (
                <span>
                  Non-recoverable commission VAT
                  <b>−{formatMoney(selected.commissionVatCost, currency)}</b>
                </span>
              ) : null}
              <span>
                Băile Felix tourist tax
                <b>
                  {selected.touristTax
                    ? `−${formatMoney(selected.touristTax, currency)}`
                    : formatMoney(0, currency)}
                </b>
              </span>
            </div>
            <div className="breakdown-total">
              <span>
                <small>Guest total for stay</small>
                <strong>{formatMoney(selected.stayClientTotal, currency)}</strong>
              </span>
              <span>
                <small>Hotel keeps for stay</small>
                <strong>{formatMoney(selected.stayHotelNet, currency)}</strong>
              </span>
            </div>
            {selectedScenario === "taxFree" ? (
              <div className="inline-alert">
                <Icon name="info" />
                <span>
                  Reference calculation only. Apply the legally required VAT and
                  local tourist tax to real bookings.
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
